import { useQuery } from "@tanstack/react-query";

import { api, type ReportScope } from "../../../lib/api";
import {
  withCardFilters,
  type DashboardLayout,
} from "../../../lib/dashboard-layout";
import { queryKeys } from "../../../lib/queryKeys";
import { DASHBOARD_STALE_MS } from "../../../lib/dashboard-refresh";
import { formatInZone } from "../../../lib/time";
import {
  Delta,
  formatMinutes,
  rangeIsReady,
  type RangeValue,
} from "../RangeFilter";
import { CardChart, ReportCard } from "../card/ReportCard";
import { TIME_SERIES_CHARTS } from "../card/chart-types";

/**
 * How long the desk takes to reply, and whether it met what it promised.
 *
 * **One median over every reply**, not a first-response line and a next-response
 * line. Those two answer genuinely different questions and the API still
 * reports both, but the question this card exists for is the plain one — "how
 * long do we take to answer" — and splitting it in two made a reader do the
 * combining in their head before they could answer it.
 *
 * Medians, never averages. One ticket left over a bank holiday weekend drags a
 * mean into fiction, and the mean is then the number somebody quotes in a
 * meeting. The percentiles are not here either: they belong on the segment
 * table at the bottom, where a tail can be read against the target it missed.
 * On this card they were four extra figures competing with the one that matters.
 *
 * Time to close is deliberately absent. With the time spent waiting on the
 * customer excluded — which it must be, or the figure measures the customer's
 * diary — what is left is the interval already reported here.
 *
 * **Every figure is business minutes**, and always has been: a response cycle
 * is recorded by `SlaService` through `responseMinutes` against the configured
 * calendar, so an overnight or a weekend has never been inside a reply time.
 * The median, the percentiles and the SLA rate all read that same column. What
 * *did* show the night was the chart's axis, which drew an empty point per
 * closed hour — fixed in `trends`, not here. The card says so in a footnote,
 * because "is this counting the weekend" is the first thing anybody asks of a
 * response-time figure and the answer was previously nowhere on screen.
 */

const SERIES = [
  { key: "overall", label: "Median reply time" },
  { key: "teamAverage", label: "Team median" },
] as const;

export function VelocityReport({
  layout,
  scope,
  compareTeam,
  isManager,
}: {
  layout: DashboardLayout;
  /** Only a manager is offered the Agent filter; the server enforces it. */
  isManager: boolean;
  scope: ReportScope;
  /** An agent's own view puts one team line beside theirs. Never a colleague's. */
  compareTeam: boolean;
}) {
  const stored = layout.card("velocity");

  /*
   * The pipeline filter is now the frame's, not this card's.
   *
   * It used to be a select of its own, stored in the card's `metric` slot and
   * single-select. It is the same question the consolidated dropdown's
   * Pipeline category asks of every card, so keeping a second control here
   * would have been two ways to answer it with no way to tell which was
   * winning. The multi-select is a strict widening: one pipeline still selects
   * one pipeline.
   */
  const scoped = withCardFilters(scope, stored);

  return (
    <ReportCard
      id="velocity"
      title="Response velocity & SLA"
      subtitle="Median time to reply — against the same point in the previous period"
      layout={layout}
      offerAgents={isManager}
      charts={TIME_SERIES_CHARTS}
      series={compareTeam ? SERIES : SERIES.slice(0, 1)}
      footnote={
        <p>
          Measured in working hours only — overnight, weekends and bank
          holidays are not counted, and hours the office was shut are left off
          the chart rather than drawn as a gap.
        </p>
      }
    >
      {({ chart, colorFor, range }) => (
        <Body
          scope={scoped}
          range={range}
          chart={chart}
          colorFor={colorFor}
          compareTeam={compareTeam}
        />
      )}
    </ReportCard>
  );
}

function Body({
  scope,
  range,
  chart,
  colorFor,
  compareTeam,
}: {
  scope: ReportScope;
  range: RangeValue;
  chart: Parameters<typeof CardChart>[0]["chart"];
  colorFor: (key: string) => string;
  compareTeam: boolean;
}) {
  const ready = rangeIsReady(range);

  const velocity = useQuery({
    queryKey: queryKeys.dashboardReport("velocity", range, scope, compareTeam),
    queryFn: () => api.velocity(range, scope, compareTeam),
    enabled: ready,
    staleTime: DASHBOARD_STALE_MS,
  });

  const trends = useQuery({
    queryKey: queryKeys.dashboardReport("trends", range, scope, compareTeam),
    queryFn: () => api.trends(range, scope, compareTeam),
    enabled: ready,
    staleTime: DASHBOARD_STALE_MS,
  });

  if (!ready) {
    return <p className="py-6 text-center text-xs text-faint">Pick both dates.</p>;
  }

  const data = velocity.data;
  const points = trends.data?.points ?? [];

  const rows = points.map((point) => ({
    label: bucketLabel(point.from, trends.data?.granularity ?? "daily"),
    values: {
      overall: point.overall,
      ...(compareTeam ? { teamAverage: point.teamAverage ?? null } : {}),
    },
  }));

  /* A duration compared in per cent, and falling is the good direction. The
     card next door compares a rate in percentage points -- the two must not be
     mixed up; see `percentChange` on the server. */
  const median = data?.current.overall.medianMinutes ?? null;
  const before = data?.previous.overall.medianMinutes ?? null;
  const change =
    median === null || before === null || before === 0
      ? null
      : ((median - before) / before) * 100;

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-faint">
            Median reply time
          </p>
          <p className="flex items-baseline gap-2">
            <span className="text-xl font-semibold tabular-nums text-content">
              {formatMinutes(median)}
            </span>
            <Delta points={change} higherIsBetter={false} suffix="%" />
          </p>
          <p className="text-[11px] text-faint">
            over {data?.current.overall.count ?? 0} replies
          </p>
        </div>

        <div>
          <p className="text-[11px] uppercase tracking-wide text-faint">
            SLA met
          </p>
          <p className="flex items-baseline gap-2">
            <span className="text-xl font-semibold tabular-nums text-content">
              {data?.current.sla.rate == null
                ? "—"
                : `${Math.round(data.current.sla.rate * 100)}%`}
            </span>
            <Delta points={data?.slaDelta ?? null} />
          </p>
          <p className="text-[11px] text-faint">
            {data ? `of ${data.current.sla.measured} measured` : " "}
          </p>
        </div>

        {/*
          "Replies per ticket" used to sit here as a mean over closed tickets,
          and it has moved out to its own card as a median with a tail -- see
          `ResponsesPerTicket`. Reply counts are long-tailed, so an average
          described no ticket anybody had and moved whenever one bad one
          landed. This card is about how *fast* the desk replies; how many
          times it has to is a different question and now has the room to be
          answered properly.
        */}
      </div>

      {compareTeam && data?.teamAverage ? (
        <p className="rounded-md bg-surface-2 px-2 py-1.5 text-[11px] text-muted-on-tint">
          Your team's median is{" "}
          <span className="text-content">
            {formatMinutes(data.teamAverage.overall.medianMinutes)}
          </span>
          . Shown for context, not as a target.
        </p>
      ) : null}

      <CardChart
        chart={chart}
        rows={rows}
        series={(compareTeam ? SERIES : SERIES.slice(0, 1)).map((entry) => ({
          ...entry,
        }))}
        colorFor={colorFor}
        format={formatMinutes}
        emptyMessage={
          velocity.isLoading ? "Loading…" : "Nothing was answered in this period."
        }
      />
    </div>
  );
}

/** A bucket's x-axis label, in London time like every other date on the desk. */
function bucketLabel(iso: string, granularity: string): string {
  const options: Intl.DateTimeFormatOptions =
    granularity === "hourly"
      ? { hour: "2-digit", minute: "2-digit" }
      : granularity === "monthly"
        ? { month: "short", year: "2-digit" }
        : { day: "numeric", month: "short" };

  return formatInZone(iso, options);
}
